import type { LearnFAQ, LearnSource } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Por qué sigo sintiéndome deprimido mientras tomo medicación?",
    answer:
      "Varias razones: la dosis puede ser muy baja, el ensayo demasiado corto, el medicamento puede no ser el adecuado, puede haber una condición no diagnosticada como el trastorno bipolar, o su depresión puede ser resistente al tratamiento. Hable con su médico sobre ajustar el tratamiento o considerar opciones sin medicación.",
  },
  {
    question: "¿Es normal seguir deprimido con antidepresivos?",
    answer:
      "Sí. Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento. Se llama depresión resistente al tratamiento. Es común y tratable: las opciones incluyen ajustes de medicación, TMS, SAINT® y SPRAVATO®.",
  },
  {
    question: "¿Qué debo hacer si mi antidepresivo no funciona?",
    answer:
      "Hable con su médico. Las opciones incluyen ajustar la dosis, cambiar de medicación, añadir un aumento o considerar TMS, SAINT® o SPRAVATO®. Una evaluación psiquiátrica puede identificar la causa. Nunca suspenda la medicación abruptamente sin supervisión.",
  },
  {
    question: "¿La TMS puede ayudar si sigo deprimido con medicación?",
    answer:
      "Sí. La TMS está autorizada por la FDA específicamente para adultos cuya depresión no ha mejorado con antidepresivos. Usa impulsos magnéticos, no medicación, y no causa efectos secundarios sistémicos. Una evaluación psiquiátrica puede determinar si la TMS es apropiada.",
  },
  {
    question: "¿Debo suspender mi medicación si no funciona?",
    answer:
      "No por su cuenta. Suspender abruptamente puede causar síntomas de discontinuación y aumentar el riesgo de recaída. Hable con su médico sobre una reducción gradual y qué probar después. Su médico puede planear la transición de forma segura.",
  },
];

const sources: LearnSource[] = [
  {
    citation:
      "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
    url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
  },
];

export const esStillDepressedOnMedication = makeEsArticle({
  slug: "why-still-depressed-while-taking-medication",
  title: "¿Por qué sigo deprimido mientras tomo medicación? | Neuro Wellness",
  description:
    "Por qué puede seguir deprimido con antidepresivos: dosis inadecuada, error diagnóstico, depresión resistente al tratamiento y qué hacer al respecto.",
  h1: "¿Por qué sigo sintiéndome deprimido mientras tomo medicación?",
  breadcrumbLabel: "Deprimido con medicación",
  shortAnswer:
    "Sentirse deprimido mientras se toman antidepresivos puede ocurrir por varias razones: la dosis puede ser muy baja, el medicamento puede no ser el adecuado para su cerebro, el ensayo puede no haber sido suficientemente largo, puede haber una condición no diagnosticada (como el trastorno bipolar) o su depresión puede ser resistente al tratamiento. La depresión resistente al tratamiento afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. Las opciones incluyen ajustes de medicación, TMS, SAINT® TMS acelerada y SPRAVATO®. Una evaluación psiquiátrica puede identificar por qué la medicación no funciona y qué probar después.",
  keyTakeaways: [
    "Sentirse deprimido con medicación es común, no inusual.",
    "La dosis puede ser muy baja o el ensayo demasiado corto.",
    "Un error diagnóstico (bipolar vs. unipolar) puede causar falta de respuesta.",
    "La depresión resistente al tratamiento afecta a ~1/3 de los pacientes tratados.",
    "TMS, SAINT® y SPRAVATO® son opciones más allá de la medicación.",
  ],
  sections: [
    {
      heading: "Razones comunes por las que la medicación no funciona",
      paragraphs: [
        "Varios factores pueden causar depresión persistente a pesar de tomar medicación:",
      ],
      list: [
        "Dosis inadecuada: el medicamento nunca se aumentó a un nivel terapéutico.",
        "Duración inadecuada: el ensayo fue demasiado corto (menos de 6-8 semanas).",
        "Medicamento equivocado: este fármaco no es el adecuado para su cerebro.",
        "Error diagnóstico: la depresión bipolar puede confundirse con la unipolar.",
        "Condiciones comórbidas: ansiedad, TEPT, consumo de sustancias o problemas médicos.",
        "Depresión resistente al tratamiento: falta de respuesta real a los tratamientos estándar.",
        "Falta de adherencia: dosis omitidas o uso inconsistente.",
      ],
    },
    {
      heading: "Qué hacer",
      paragraphs: [
        "Si sigue deprimido mientras toma medicación, hable con su médico. Las opciones incluyen ajustar la dosis, cambiar a otra clase de medicación, añadir un medicamento de aumento o considerar tratamientos sin medicación como TMS, SAINT® o SPRAVATO®. Una evaluación psiquiátrica integral puede identificar la causa y recomendar los siguientes pasos.",
        "Nunca suspenda la medicación abruptamente sin supervisión: la discontinuación puede causar síntomas de abstinencia y aumentar el riesgo de recaída.",
      ],
    },
    {
      heading: "Cuándo considerar tratamientos sin medicación",
      paragraphs: [
        "Si ha probado varios antidepresivos en dosis y duraciones adecuadas sin éxito, los tratamientos sin medicación pueden ser apropiados. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo acelerado de cinco días para la depresión resistente al tratamiento. SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. Actúan por mecanismos distintos a los antidepresivos orales.",
      ],
    },
  ],
  faqs,
  sources,
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
